/**
 * Central, kind-aware status-chip resolver for project artifacts.
 *
 * One function, one vocab — every surface (project artifact list,
 * card, datagrid, future detail-page chips) reads from here so the
 * cohort / AV / concept-group / dataset status mapping never
 * diverges across the app.
 *
 * AV resolution delegates to ``resolveVariableStatusChip`` from
 * ``feature-analytical-variables`` (the per-store mapping lives next
 * to the AV detail wire shape — see PR #3666). Cohort resolution is
 * a 1:1 pass-through of ``CohortDefinitionStatus``: the cohort
 * lifecycle vocabulary is a deliberate product decision, so we
 * surface those tokens as-is rather than translating them into the
 * AV vocab.
 *
 * Analytical datasets and analyses share the cohort lifecycle vocabulary
 * (DRAFT / PROCESSING / PROCESSED / ERROR). Concept-groups have no lifecycle
 * today and return ``null`` so the caller can render a placeholder.
 *
 * Chip union members:
 * - AV vocab (from PR #3666): ``working`` / ``drafting`` / ``verified`` /
 *   ``error`` / ``saved``
 * - Cohort vocab (mirrors ``CohortDefinitionStatus``): ``draft`` /
 *   ``unsaved`` / ``processing`` / ``processed`` / ``final`` /
 *   ``error`` (shared token with the AV vocab)
 * - ``unknown`` — raw status doesn't map; consumer renders the raw
 *   string verbatim
 */

import {
  resolveVariableStatusChip,
  type VariableStatusChip,
} from '@verantos-dev/feature-analytical-variables';

import type { ProjectArtifact } from '../types';

/**
 * 1:1 with ``CohortDefinitionStatus`` (``types-cohorts``). Kept as a
 * literal union (not re-imported) so consumers don't pick up a runtime
 * dependency on the types package just to render a chip.
 */
export type CohortStatusChip = 'draft' | 'unsaved' | 'processing' | 'processed' | 'final' | 'error';

export type ArtifactStatusChip = VariableStatusChip | CohortStatusChip | 'unknown';

export interface ResolvedArtifactStatus {
  chip: ArtifactStatusChip;
  /** Human-readable label rendered on the chip. */
  label: string;
}

const CHIP_LABEL: Record<Exclude<ArtifactStatusChip, 'unknown'>, string> = {
  // AV vocab — see ``resolveVariableStatusChip`` in PR #3666.
  working: 'Working…',
  drafting: 'Drafting',
  verified: 'Verified',
  error: 'Error',
  saved: 'Saved',
  // Cohort vocab — title-case of ``CohortDefinitionStatus``.
  draft: 'Draft',
  unsaved: 'Unsaved',
  processing: 'Processing',
  processed: 'Processed',
  final: 'Final',
};

/**
 * Cohort lifecycle (``CohortDefinitionStatus``) → chip vocab.
 *
 * 1:1 mapping. The cohort lifecycle vocabulary is a deliberate
 * product decision — we surface the raw status rather than folding
 * it into the AV chip vocab. Unmapped values fall through to
 * ``unknown`` so the caller can render the raw status string.
 */
export const resolveCohortStatusChip = (
  rawStatus: string | null | undefined
): ArtifactStatusChip => {
  if (!rawStatus) return 'unknown';
  switch (rawStatus.toUpperCase()) {
    case 'DRAFT':
      return 'draft';
    case 'UNSAVED':
      return 'unsaved';
    case 'PROCESSING':
      return 'processing';
    case 'PROCESSED':
      return 'processed';
    case 'FINAL':
      return 'final';
    case 'ERROR':
      return 'error';
    default:
      return 'unknown';
  }
};

/**
 * Kind-dispatched chip resolver. Returns ``null`` when the artifact
 * kind has no lifecycle to surface (concept groups today) — callers
 * decide whether to render a placeholder.
 */
export const resolveArtifactStatusChip = (
  artifact: ProjectArtifact,
  options: { isStreaming?: boolean } = {}
): ResolvedArtifactStatus | null => {
  switch (artifact.kind) {
    // AD and analysis share the cohort lifecycle tokens, so the cohort
    // resolver covers all three.
    case 'cohort':
    case 'analytical_dataset':
    case 'analysis': {
      const chip = resolveCohortStatusChip(artifact.status);
      return {
        chip,
        label: chip === 'unknown' ? artifact.status || '—' : CHIP_LABEL[chip],
      };
    }
    case 'variable': {
      // ``kindSpecific.source`` is set by the manifest mapper from the
      // backend's ``VariableArtifactMetadata.source``. Falling back to
      // ``null`` makes the AV resolver default to ``drafting`` (the
      // chip vocab's "we don't know what tier this is" position).
      const source = artifact.kindSpecific?.source as 'library' | 'knowledge' | null | undefined;
      const chip = resolveVariableStatusChip(
        source ? { source, status: artifact.status || null } : null,
        { isStreaming: options.isStreaming }
      );
      return { chip, label: CHIP_LABEL[chip] };
    }
    case 'concept_group':
      return null;
  }
};
